import { animateThemeToggle } from './animationSystem.js';

/**
 * Dark Mode Controller
 * Manages theme switching with system preference detection
 */
export class DarkModeController {
    constructor() {
        this.storageKey = 'taxonomic-theme';
        this.currentTheme = null;
        this.mediaQuery = null;
    }

    /**
     * Initialize dark mode system
     */
    init() {
        // Load saved theme or detect system preference
        const savedTheme = this.loadSavedTheme();
        const systemTheme = this.getSystemTheme();

        // Use saved theme if available, otherwise use system theme
        this.currentTheme = savedTheme || systemTheme;

        // Apply the theme
        this.applyTheme(this.currentTheme);

        // Watch for system preference changes
        this.watchSystemTheme();
    }

    /**
     * Load saved theme from localStorage
     * @returns {string|null} Saved theme ('light' or 'dark') or null
     */
    loadSavedTheme() {
        try {
            return localStorage.getItem(this.storageKey);
        } catch (e) {
            console.warn('Failed to load saved theme:', e);
            return null;
        }
    }

    /**
     * Save theme to localStorage
     * @param {string} theme - Theme to save ('light' or 'dark')
     */
    saveTheme(theme) {
        try {
            localStorage.setItem(this.storageKey, theme);
        } catch (e) {
            console.warn('Failed to save theme:', e);
        }
    }

    /**
     * Get system theme preference
     * @returns {string} System theme ('light' or 'dark')
     */
    getSystemTheme() {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    }

    /**
     * Apply theme to document
     * @param {string} theme - Theme to apply ('light' or 'dark')
     */
    applyTheme(theme) {
        if (theme === 'dark') {
            document.documentElement.setAttribute('data-theme', 'dark');
        } else {
            document.documentElement.removeAttribute('data-theme');
        }
        this.currentTheme = theme;
    }

    /**
     * Toggle between light and dark themes
     */
    toggle() {
        const newTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
        const toggleButton = document.getElementById('theme-toggle');

        // Animate the toggle with circular wipe
        if (toggleButton) {
            animateThemeToggle(toggleButton, newTheme === 'dark');
        }

        this.applyTheme(newTheme);
        this.saveTheme(newTheme);

        // Announce to screen readers
        this.announceThemeChange(newTheme);
    }

    /**
     * Watch for system theme changes
     */
    watchSystemTheme() {
        if (!window.matchMedia) return;

        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        // Modern browsers
        if (this.mediaQuery.addEventListener) {
            this.mediaQuery.addEventListener('change', (e) => {
                // Only auto-switch if user hasn't set a preference
                const savedTheme = this.loadSavedTheme();
                if (!savedTheme) {
                    const newTheme = e.matches ? 'dark' : 'light';
                    this.applyTheme(newTheme);
                }
            });
        }
        // Legacy browsers
        else if (this.mediaQuery.addListener) {
            this.mediaQuery.addListener((e) => {
                const savedTheme = this.loadSavedTheme();
                if (!savedTheme) {
                    const newTheme = e.matches ? 'dark' : 'light';
                    this.applyTheme(newTheme);
                }
            });
        }
    }

    /**
     * Get current theme
     * @returns {string} Current theme ('light' or 'dark')
     */
    getTheme() {
        return this.currentTheme;
    }

    /**
     * Announce theme change to screen readers
     * @param {string} theme - New theme
     */
    announceThemeChange(theme) {
        const announcer = document.getElementById('announcer');
        if (announcer) {
            announcer.textContent = `${theme === 'dark' ? 'Dark' : 'Light'} mode enabled`;
        }
    }
}
